/**
 * Phase 10f.1: Onboarding Wizard
 *
 * Full-screen overlay wizard shown to tenants with onboarding_status=pending|in_progress.
 * 4 steps: Firm Type → Company Info → Invite Team → Ready
 *
 * Mounts inside DashboardLayout so it renders on top of the normal app (z-50).
 * Partial progress persisted to localStorage so refresh doesn't lose state.
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { ONBOARDING_QUERY_KEY } from '@/hooks/useOnboardingStatus';
import {
  Building2,
  HardHat,
  Wrench,
  Users,
  Settings2,
  Check,
  ChevronRight,
  ChevronLeft,
  Plus,
  X,
  FileText,
  Receipt,
  BookOpen,
  MapPin,
  HelpCircle,
  Globe,
  CheckSquare,
  BarChart2,
  Folder,
  Upload,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

// ============================================================================
// Constants
// ============================================================================

const STORAGE_KEY = 'onboarding_wizard_v1';
const MAX_INVITES = 10;

const STEPS = [
  { id: 'firm-type', label: 'Firm Type' },
  { id: 'company',   label: 'Company' },
  { id: 'team',      label: 'Team' },
  { id: 'ready',     label: "You're set!" },
] as const;

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// ============================================================================
// Firm Types
// ============================================================================

interface FirmTypeOption {
  id: string;
  icon: LucideIcon;
  title: string;
  description: string;
}

const FIRM_TYPES: FirmTypeOption[] = [
  {
    id: 'architect',
    icon: Building2,
    title: 'Architecture Firm',
    description: 'Proposals, deliverables, and construction administration',
  },
  {
    id: 'general_contractor',
    icon: HardHat,
    title: 'General Contractor',
    description: 'Site visits, RFIs, submittals, and field operations',
  },
  {
    id: 'engineering_consultant',
    icon: Wrench,
    title: 'Engineering Consultant',
    description: 'Project delivery, client portals, and document management',
  },
  {
    id: 'owner_rep',
    icon: Users,
    title: "Owner's Rep",
    description: 'Project oversight, reporting, and stakeholder coordination',
  },
  {
    id: 'other',
    icon: Settings2,
    title: 'Other',
    description: "I'll configure my own modules",
  },
];

// ============================================================================
// Ready-screen action cards per firm type
// ============================================================================

interface ReadyAction {
  icon: LucideIcon;
  title: string;
  url: string;
}

const READY_ACTIONS: Record<string, ReadyAction[]> = {
  architect: [
    { icon: FileText,    title: 'Create a proposal',               url: '/projects/new' },
    { icon: BookOpen,    title: 'Set up your fee letter template',  url: '/settings?tab=templates' },
    { icon: Receipt,     title: 'Send your first invoice',          url: '/invoices/new' },
  ],
  general_contractor: [
    { icon: Folder,      title: 'Create your first project',        url: '/projects/new' },
    { icon: MapPin,      title: 'Log a site visit',                 url: '/projects' },
    { icon: HelpCircle,  title: 'Track an RFI',                    url: '/projects' },
  ],
  engineering_consultant: [
    { icon: Folder,      title: 'Create a project',                 url: '/projects/new' },
    { icon: Globe,       title: 'Set up a client portal',           url: '/projects' },
    { icon: CheckSquare, title: 'Add deliverables',                 url: '/projects' },
  ],
  owner_rep: [
    { icon: Folder,      title: 'Create your first project',        url: '/projects/new' },
    { icon: BarChart2,   title: 'View reports',                     url: '/reports' },
    { icon: Users,       title: 'Invite your team',                 url: '/settings' },
  ],
  other: [
    { icon: Folder,      title: 'Create your first project',        url: '/projects/new' },
    { icon: Settings2,   title: 'Configure settings',               url: '/settings' },
    { icon: Users,       title: 'Invite your team',                 url: '/settings' },
  ],
};

// ============================================================================
// Wizard state (persisted to localStorage)
// ============================================================================

interface InviteEntry {
  email: string;
  role: 'admin' | 'member';
}

interface WizardState {
  step: number;
  firmType: string | null;
  companyName: string;
  address: string;
  fiscalYearStartMonth: number;
  logoUrl: string | null;
  inviteEntries: InviteEntry[];
}

const DEFAULT_STATE: WizardState = {
  step: 0,
  firmType: null,
  companyName: '',
  address: '',
  fiscalYearStartMonth: 1,
  logoUrl: null,
  inviteEntries: [
    { email: '', role: 'member' },
    { email: '', role: 'member' },
    { email: '', role: 'member' },
  ],
};

// ============================================================================
// Step indicator
// ============================================================================

function WizardProgress({ currentStep }: { currentStep: number }) {
  return (
    <nav aria-label="Onboarding progress">
      <ol role="list" className="flex items-center justify-center space-x-4 py-6 px-4">
        {STEPS.map((step, index) => {
          const isComplete = index < currentStep;
          const isCurrent = index === currentStep;

          return (
            <li key={step.id} className="flex items-center">
              <div
                className={cn(
                  'flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium transition-colors',
                  isComplete && 'bg-primary text-primary-foreground',
                  isCurrent && 'border-2 border-primary text-primary',
                  !isComplete && !isCurrent && 'border border-muted-foreground/30 text-muted-foreground'
                )}
              >
                {isComplete ? <Check className="w-4 h-4" /> : index + 1}
              </div>
              <span className={cn(
                'ml-2 text-sm font-medium hidden sm:block',
                isCurrent && 'text-primary',
                !isCurrent && 'text-muted-foreground'
              )}>
                {step.label}
              </span>
              {index < STEPS.length - 1 && (
                <ChevronRight className="w-4 h-4 mx-2 text-muted-foreground/40" />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

// ============================================================================
// Step 1: Firm Type
// ============================================================================

interface FirmTypeStepProps {
  selected: string | null;
  onSelect: (id: string) => void;
}

function FirmTypeStep({ selected, onSelect }: FirmTypeStepProps) {
  return (
    <div data-testid="wizard-step-firm-type" className="space-y-8 w-full max-w-2xl mx-auto">
      <div className="text-center">
        <h2 className="text-2xl font-semibold">What kind of firm are you?</h2>
        <p className="text-muted-foreground mt-2">We'll tailor your experience to match how you work.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {FIRM_TYPES.map((firm) => {
          const Icon = firm.icon;
          const isSelected = selected === firm.id;
          return (
            <button
              key={firm.id}
              onClick={() => onSelect(firm.id)}
              className={cn(
                'p-5 rounded-xl border text-left transition-all hover:shadow-md',
                isSelected
                  ? 'border-primary bg-primary/5 ring-2 ring-primary shadow-sm'
                  : 'border-border hover:border-primary/50 hover:bg-muted/30'
              )}
            >
              <Icon className={cn('w-7 h-7 mb-3', isSelected ? 'text-primary' : 'text-muted-foreground')} />
              <div className="font-semibold text-sm">{firm.title}</div>
              <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{firm.description}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// Step 2: Company Info
// ============================================================================

interface CompanyInfoStepProps {
  companyName: string;
  address: string;
  fiscalYearStartMonth: number;
  logoUrl: string | null;
  onCompanyNameChange: (v: string) => void;
  onAddressChange: (v: string) => void;
  onFiscalYearChange: (v: number) => void;
  onLogoChange: (url: string | null) => void;
  logoFileRef: React.MutableRefObject<File | null>;
}

function CompanyInfoStep({
  companyName,
  address,
  fiscalYearStartMonth,
  logoUrl,
  onCompanyNameChange,
  onAddressChange,
  onFiscalYearChange,
  onLogoChange,
  logoFileRef,
}: CompanyInfoStepProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFile = (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    logoFileRef.current = file;
    const previewUrl = URL.createObjectURL(file);
    onLogoChange(previewUrl);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  return (
    <div data-testid="wizard-step-company" className="space-y-6 w-full max-w-lg mx-auto">
      <div className="text-center">
        <h2 className="text-2xl font-semibold">Tell us about your company</h2>
        <p className="text-muted-foreground mt-2">This appears on your invoices and proposals.</p>
      </div>

      <div className="space-y-4">
        {/* Company name */}
        <div className="space-y-2">
          <Label htmlFor="company-name">Company name *</Label>
          <Input
            id="company-name"
            value={companyName}
            onChange={(e) => onCompanyNameChange(e.target.value)}
            placeholder="e.g. Acme Architecture Inc."
          />
        </div>

        {/* Logo upload */}
        <div className="space-y-2">
          <Label>Logo (optional)</Label>
          <div
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={cn(
              'border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors',
              isDragging ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 hover:bg-muted/20'
            )}
          >
            {logoUrl ? (
              <div className="flex items-center justify-center gap-3">
                <img src={logoUrl} alt="Logo preview" className="h-12 object-contain rounded" />
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onLogoChange(null); logoFileRef.current = null; }}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2 text-muted-foreground">
                <Upload className="w-6 h-6" />
                <span className="text-sm">Drop your logo here or <span className="text-primary underline underline-offset-2">browse</span></span>
                <span className="text-xs">PNG, JPG, SVG up to 2MB</span>
              </div>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
          />
        </div>

        {/* Address */}
        <div className="space-y-2">
          <Label htmlFor="address">Address (optional)</Label>
          <Input
            id="address"
            value={address}
            onChange={(e) => onAddressChange(e.target.value)}
            placeholder="e.g. 123 Main St, Toronto, ON"
          />
        </div>

        {/* Fiscal year */}
        <div className="space-y-2">
          <Label htmlFor="fiscal-year">Fiscal year starts</Label>
          <select
            id="fiscal-year"
            value={fiscalYearStartMonth}
            onChange={(e) => onFiscalYearChange(Number(e.target.value))}
            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            {MONTHS.map((month, index) => (
              <option key={month} value={index + 1}>{month}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Step 3: Invite Team
// ============================================================================

interface InviteTeamStepProps {
  entries: InviteEntry[];
  onEntriesChange: (entries: InviteEntry[]) => void;
}

function InviteTeamStep({ entries, onEntriesChange }: InviteTeamStepProps) {
  const updateEntry = (index: number, field: keyof InviteEntry, value: string) => {
    const next = entries.map((e, i) =>
      i === index ? { ...e, [field]: value } : e
    );
    onEntriesChange(next);
  };

  const addEntry = () => {
    if (entries.length < MAX_INVITES) {
      onEntriesChange([...entries, { email: '', role: 'member' }]);
    }
  };

  const removeEntry = (index: number) => {
    onEntriesChange(entries.filter((_, i) => i !== index));
  };

  return (
    <div data-testid="wizard-step-invite" className="space-y-6 w-full max-w-lg mx-auto">
      <div className="text-center">
        <h2 className="text-2xl font-semibold">Invite your team</h2>
        <p className="text-muted-foreground mt-2">Add teammates now or skip and invite them later.</p>
      </div>

      <div className="space-y-3">
        {entries.map((entry, index) => (
          <div key={index} className="flex gap-2 items-center">
            <Input
              type="email"
              value={entry.email}
              onChange={(e) => updateEntry(index, 'email', e.target.value)}
              placeholder={`teammate${index + 1}@company.com`}
              className="flex-1"
              aria-label={`Invite email ${index + 1}`}
            />
            <select
              value={entry.role}
              onChange={(e) => updateEntry(index, 'role', e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              aria-label={`Role for invite ${index + 1}`}
            >
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </select>
            {entries.length > 1 && (
              <button
                type="button"
                onClick={() => removeEntry(index)}
                className="text-muted-foreground hover:text-foreground p-1"
                aria-label={`Remove invite ${index + 1}`}
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}

        {entries.length < MAX_INVITES && (
          <button
            type="button"
            onClick={addEntry}
            className="flex items-center gap-1 text-sm text-primary hover:underline underline-offset-2"
          >
            <Plus className="w-4 h-4" />
            Add another
          </button>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Step 4: Ready Screen
// ============================================================================

interface ReadyStepProps {
  companyName: string;
  firmType: string | null;
  onActionClick: (url: string) => void;
}

function ReadyStep({ companyName, firmType, onActionClick }: ReadyStepProps) {
  const actions = READY_ACTIONS[firmType ?? 'other'] ?? READY_ACTIONS['other'];
  const displayName = companyName.trim() || undefined;

  return (
    <div data-testid="wizard-step-ready" className="space-y-8 w-full max-w-xl mx-auto text-center">
      <div>
        <div className="text-5xl mb-4">🎉</div>
        <h2 className="text-2xl font-semibold">
          {displayName ? `You're all set, ${displayName}!` : "You're all set!"}
        </h2>
        <p className="text-muted-foreground mt-2">Here are your first steps to get started.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {actions.map((action) => {
          const Icon = action.icon;
          return (
            <button
              key={action.title}
              onClick={() => onActionClick(action.url)}
              className="p-5 rounded-xl border border-border hover:border-primary/50 hover:bg-muted/30 hover:shadow-md text-left transition-all group"
            >
              <Icon className="w-6 h-6 mb-3 text-muted-foreground group-hover:text-primary transition-colors" />
              <div className="text-sm font-medium leading-snug">{action.title}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

interface OnboardingWizardProps {
  initialFirmType?: string | null;
}

export function OnboardingWizard({ initialFirmType }: OnboardingWizardProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const logoFileRef = useRef<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Restore state from localStorage (or default)
  const [state, setState] = useState<WizardState>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved) as Partial<WizardState>;
        // Merge with defaults to handle missing fields from older saves
        const merged: WizardState = { ...DEFAULT_STATE, ...parsed };
        // Pre-fill firmType from server if not already set
        if (!merged.firmType && initialFirmType) {
          merged.firmType = initialFirmType;
        }
        return merged;
      }
    } catch {
      // ignore parse errors
    }
    return {
      ...DEFAULT_STATE,
      firmType: initialFirmType ?? null,
    };
  });

  // Persist state to localStorage on every change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  const update = (patch: Partial<WizardState>) =>
    setState((s) => ({ ...s, ...patch }));

  const canGoNext = () => {
    switch (state.step) {
      case 0: return !!state.firmType;
      case 1: return state.companyName.trim().length > 0;
      case 2: return true;
      default: return false;
    }
  };

  const handleNext = () => {
    if (canGoNext() && state.step < STEPS.length - 1) {
      update({ step: state.step + 1 });
    }
  };

  const handleBack = () => {
    if (state.step > 0) {
      update({ step: state.step - 1 });
    }
  };

  const handleSkip = async () => {
    try {
      await api.onboarding.skip();
    } catch {
      // best-effort — hide wizard regardless
    }
    localStorage.removeItem(STORAGE_KEY);
    queryClient.invalidateQueries({ queryKey: ONBOARDING_QUERY_KEY });
  };

  const finishWizard = async (navigateTo: string) => {
    setIsSubmitting(true);
    try {
      await api.onboarding.setup({
        firm_type: state.firmType!,
        company_name: state.companyName.trim() || undefined,
        logo_url: state.logoUrl ?? undefined,
        address: state.address.trim() || undefined,
        fiscal_year_start_month: state.fiscalYearStartMonth,
      });
      await api.onboarding.complete();
      localStorage.removeItem(STORAGE_KEY);
      queryClient.invalidateQueries({ queryKey: ONBOARDING_QUERY_KEY });
      // Also refresh tenant branding (company_name/logo may have changed)
      queryClient.invalidateQueries({ queryKey: ['tenant-branding'] });
      navigate(navigateTo);
    } catch {
      toast.error('Setup failed. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleComplete = () => finishWizard('/dashboard');
  const handleActionClick = (url: string) => finishWizard(url);

  const isLastStep = state.step === STEPS.length - 1;

  const renderStep = () => {
    switch (state.step) {
      case 0:
        return (
          <FirmTypeStep
            selected={state.firmType}
            onSelect={(id) => update({ firmType: id })}
          />
        );
      case 1:
        return (
          <CompanyInfoStep
            companyName={state.companyName}
            address={state.address}
            fiscalYearStartMonth={state.fiscalYearStartMonth}
            logoUrl={state.logoUrl}
            onCompanyNameChange={(v) => update({ companyName: v })}
            onAddressChange={(v) => update({ address: v })}
            onFiscalYearChange={(v) => update({ fiscalYearStartMonth: v })}
            onLogoChange={(url) => update({ logoUrl: url })}
            logoFileRef={logoFileRef}
          />
        );
      case 2:
        return (
          <InviteTeamStep
            entries={state.inviteEntries}
            onEntriesChange={(entries) => update({ inviteEntries: entries })}
          />
        );
      case 3:
        return (
          <ReadyStep
            companyName={state.companyName}
            firmType={state.firmType}
            onActionClick={handleActionClick}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-background flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-label="Setup wizard"
    >
      {/* Step progress */}
      <div className="border-b">
        <WizardProgress currentStep={state.step} />
      </div>

      {/* Step content */}
      <div className="flex-1 overflow-y-auto flex items-start justify-center px-4 py-10">
        {renderStep()}
      </div>

      {/* Navigation footer */}
      <div className="border-t px-6 py-4 flex items-center justify-between gap-4">
        <Button
          variant="outline"
          onClick={handleBack}
          disabled={state.step === 0 || isSubmitting}
          className="gap-1"
        >
          <ChevronLeft className="w-4 h-4" />
          Back
        </Button>

        <button
          type="button"
          onClick={handleSkip}
          disabled={isSubmitting}
          className="text-sm text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
        >
          Skip for now
        </button>

        {isLastStep ? (
          <Button onClick={handleComplete} disabled={isSubmitting}>
            {isSubmitting ? 'Saving…' : 'Go to Dashboard'}
          </Button>
        ) : (
          <Button onClick={handleNext} disabled={!canGoNext() || isSubmitting} className="gap-1">
            Continue
            <ChevronRight className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
